import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import StaticAdsService from 'services/StaticAdsService';

import { addMissingHintLines, diffSlots, hintsToText, parseHints, removeOrphanHintLines } from './formatSlots';
import { aspectOptions, aspectLabel } from './staticAspects';
import { StaticFormat } from './types';

// Create or edit one format.
//
// A built-in's identity (name, family, aspect) is code-owned: an edit may change
// what a format SAYS, never what it IS, so those fields are disabled with the
// escape hatch named — Duplicate gives a fully editable copy. Custom formats own
// everything but their key, which a generation stores forever.

const MONO_INPUT = { fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.5 } as const;

// Mirrors FORMAT_FAMILIES in the collector's staticFormats.js. The server
// validates against its own list and 400s an unknown one; this only decides what
// the picker offers.
const FAMILIES = [
  'comparison', 'copy-led', 'educational', 'editorial', 'lifestyle', 'offer', 'social-proof', 'ugc',
];

const BLANK = {
  label: '',
  family: 'copy-led',
  defaultAspect: '1:1',
  description: '',
  skeleton: '',
  hintsText: '',
};

export default function FormatEditorDialog({ open, format, service, onClose, onSaved }: {
  open: boolean;
  /** null = create a new custom format. */
  format: StaticFormat | null;
  service: StaticAdsService;
  onClose: () => void;
  onSaved: (saved: StaticFormat) => void;
}) {
  const [label, setLabel] = useState(BLANK.label);
  const [family, setFamily] = useState(BLANK.family);
  const [defaultAspect, setDefaultAspect] = useState(BLANK.defaultAspect);
  const [description, setDescription] = useState(BLANK.description);
  const [skeleton, setSkeleton] = useState(BLANK.skeleton);
  const [hintsText, setHintsText] = useState(BLANK.hintsText);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isBuiltin = format?.source === 'builtin';
  const isNew = !format;

  // Seeded on OPEN only, keyed by the format being edited. Re-seeding on every
  // render would throw away what the operator is typing the moment anything
  // upstream refreshed.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSaving(false);
    if (!format) {
      setLabel(BLANK.label);
      setFamily(BLANK.family);
      setDefaultAspect(BLANK.defaultAspect);
      setDescription(BLANK.description);
      setSkeleton(BLANK.skeleton);
      setHintsText(BLANK.hintsText);
      return;
    }
    setLabel(format.label);
    setFamily(format.family);
    setDefaultAspect(format.defaultAspect);
    setDescription(format.description);
    setSkeleton(format.skeleton);
    setHintsText(hintsToText(format.slotHints));
  }, [open, format]);

  const hints = useMemo(() => parseHints(hintsText), [hintsText]);
  const diff = useMemo(() => diffSlots(skeleton, hints), [skeleton, hints]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = { description, skeleton, slotHints: hints };
      const resp = isNew
        ? await service.createStaticFormat({ ...body, label, family, defaultAspect })
        : await service.updateStaticFormat(
          format!.key,
          isBuiltin ? body : { ...body, label, family, defaultAspect },
        );
      onSaved(resp.format);
      onClose();
    } catch (err: any) {
      // The server is the authority on the skeleton's grammar — its message
      // names the exact slots, so it is shown verbatim rather than summarised.
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {isNew ? 'New format' : `Edit ${format!.label}`}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {isBuiltin && (
            <Alert severity="info">
              This is a built-in format. Its wording is yours to edit and you can reset it to the
              original at any time — its <strong>name, family and aspect</strong> are set in code.
              Duplicate it to get a fully editable copy.
            </Alert>
          )}

          <Stack direction="row" spacing={1.5}>
            <Tooltip title={isBuiltin ? 'Set in code — duplicate this format to rename it' : ''}>
              <TextField
                size="small" label="Name" value={label} disabled={isBuiltin} sx={{ flex: 2 }}
                onChange={e => setLabel(e.target.value)}
              />
            </Tooltip>
            <TextField
              select size="small" label="Family" value={family} disabled={isBuiltin} sx={{ flex: 1 }}
              onChange={e => setFamily(e.target.value)}
              helperText="Groups the library"
            >
              {FAMILIES.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label="Aspect" value={defaultAspect} disabled={isBuiltin} sx={{ flex: 1 }}
              onChange={e => setDefaultAspect(e.target.value)}
            >
              {aspectOptions(defaultAspect).map(a => (
                <MenuItem key={a} value={a}>{aspectLabel(a)}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <TextField
            size="small" label="Description" value={description} fullWidth
            onChange={e => setDescription(e.target.value)}
            helperText="The one plain-language line shown when picking a format"
          />

          <TextField
            size="small" label="Skeleton" value={skeleton} fullWidth multiline minRows={8}
            onChange={e => setSkeleton(e.target.value)}
            InputProps={{ sx: MONO_INPUT }}
            helperText={'Dense prose describing the composition. Put every literal on-image string on its own line '
              + 'as: Rendered text: "...". Use {slot} where the model should write copy. Leave out any '
              + '"use the attached images" opener and any aspect-ratio sentence — those are added around it.'}
          />

          <TextField
            size="small" label="Slot hints" value={hintsText} fullWidth multiline minRows={4}
            onChange={e => setHintsText(e.target.value)}
            InputProps={{ sx: MONO_INPUT }}
            helperText="One per line, as: slot: what the model should write there"
          />

          {/* Shown, never enforced. This regex is a copy of the server's, and a
              client gate built on a copy blocks legitimate saves the moment the
              two drift — the server's 400 is the authority. */}
          {diff.missing.length > 0 && (
            <Alert
              severity="warning"
              action={(
                <Button color="inherit" size="small"
                  onClick={() => setHintsText(addMissingHintLines(hintsText, diff.missing))}
                >
                  Add hint lines
                </Button>
              )}
            >
              {diff.missing.length} slot{diff.missing.length > 1 ? 's have' : ' has'} no hint:{' '}
              {diff.missing.map(s => `{${s}}`).join(', ')} — the save will be rejected.
            </Alert>
          )}
          {diff.orphan.length > 0 && (
            <Alert
              severity="info"
              action={(
                <Button color="inherit" size="small"
                  onClick={() => setHintsText(removeOrphanHintLines(hintsText, diff.orphan))}
                >
                  Remove them
                </Button>
              )}
            >
              {diff.orphan.join(', ')} — no longer in the skeleton, so {diff.orphan.length > 1 ? 'these' : 'this'}{' '}
              will be dropped on save.
            </Alert>
          )}
          {!diff.missing.length && !diff.orphan.length && skeleton.trim() && (
            <Typography variant="caption" color="text.secondary">
              Every slot has a hint.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : (isNew ? 'Create format' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
